import type { RoundConfiguration } from "Models";
import {
  roundPresetSummary,
  type PresetGroupTitle,
} from "./roundPresetSummary";

/**
 * Read-only view of a saved Round Preset's settings, shown when a row's View
 * button is expanded. Styled to sit inside the existing management list rather
 * than to stand out from it.
 */
const RoundConfigurationSettingsView: React.FC<{
  config: RoundConfiguration;
  /**
   * Groups to show, in case only part of a preset applies. Leagues seed from
   * the conditions and automation only, so showing a preset's course or
   * handicap there would describe settings the rounds never take. Omitted
   * shows the whole preset, which is what the Round Presets screen wants.
   */
  include?: readonly PresetGroupTitle[];
}> = ({ config, include }) => {
  const groups = roundPresetSummary(config).filter(
    (g) => !include || include.includes(g.title),
  );

  if (groups.length === 0) {
    return (
      <div className="w-full px-2 py-3 text-sm text-gray-500 dark:text-gray-300">
        This preset has no saved settings to show.
      </div>
    );
  }

  return (
    <div className="grid w-full grid-cols-1 gap-x-8 gap-y-4 px-2 py-3">
      {groups.map((group) => (
        <div key={group.title} className="flex flex-col">
          <div className="brand-subHeading pb-1 text-sm font-semibold text-gray-600 dark:text-gray-50">
            {group.title}
          </div>
          <dl className="flex flex-col gap-1">
            {group.rows.map((row) => (
              <div
                key={row.label}
                className="flex flex-row justify-between gap-4 text-sm"
              >
                <dt className="text-gray-500 dark:text-gray-300">
                  {row.label}
                </dt>
                <dd className="truncate text-right text-gray-800 dark:text-white">
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
};

export default RoundConfigurationSettingsView;
